﻿import { State } from '@progress/kendo-data-query';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { useCallback, useState } from 'react';
import Coordinate from '../../../../common/coordinate';
import { GetLastKnowLocationTypeEnum } from '../../../../common/enums';
import { FlightCurrentPositionDto } from '../../../../flights/interop/flightDto';
import { MessageType } from '../../../../services/actor-messages';
import { LocationApiService } from '../../../../services/axios/axios-api-generated';
import { AirportWithDistance } from '../../../../static/static-airports';
import { useFlightWorker } from '../../../hooks/use-flight-worker';
import { useNearestCity } from './use-nearest-city';

export interface AirportInfo {
    name?: string;
    location?: Coordinate;
    icao?: string;
    iata?: string;
    faa?: string;
}

export interface CityLocationDetails {
    name: string;
    distance: number | null;
    fromBearing: number | null;
}

export interface LastKnownLocationInfo {
    position: Coordinate;
    time: Date | null;
    origin: string | null;
    destination: string | null;
    originAirport: AirportInfo | null;
    destinationAirport?: AirportInfo | null;
    distanceToDestination: number | null;
    isAtDestination: boolean;
    nearestCity: CityLocationDetails | null;
    nearestAirport: AirportWithDistance | null;
}

interface UseFlightPositionResult {
    currentPosition: FlightCurrentPosition | null;
    lastKnownLocation: LastKnownLocationInfo | null;
    isLoading: boolean;
    error: Error | null;
    fetchFlightPosition: (acid: string) => Promise<void>;
    clear: () => void;
}

export interface FlightCurrentPosition extends Omit<FlightCurrentPositionDto, 'destinationAirport' | 'originAirport' | 'arrivalTime'> {
    nearestCity: CityLocationDetails | null;
    nearestAirport: AirportWithDistance | null;
    destinationAirport: AirportInfo | null;
    originAirport?: AirportInfo | null;
    arrivalTime: Date | null;
}

export function useFlightPosition(): UseFlightPositionResult {
    const { getNearestCity } = useNearestCity();
    const [currentPosition, setCurrentPosition] = useState<FlightCurrentPosition | null>(null);
    const [lastKnownLocation, setLastKnownLocation] = useState<LastKnownLocationInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const worker = useFlightWorker();

    const getAirportInfo = useCallback(
        async (airportId?: string): Promise<AirportInfo | undefined> => {
            if (!airportId) {
                return undefined;
            }

            try {
                const airport = await worker.actor.sendAsync({
                    type: MessageType.findAirport,
                    data: airportId
                });

                if (airport) {
                    return {
                        name: airport.name,
                        location: new Coordinate(airport.lat ?? 200, airport.lng ?? 200),
                        icao: airport.icao,
                        iata: airport.iata,
                        faa: airport.faa
                    };
                }
            } catch (err) {
                console.error(`Failed to fetch airport ${airportId}:`, err);
            }

            return undefined;
        },
        [worker]
    );

    const createAirportExclusionFilters = useCallback((airport: AirportInfo) => {
        const filters = [];

        if (airport.icao && airport.icao.trim() !== '') {
            filters.push({
                field: 'icao',
                operator: 'neq',
                value: airport.icao
            });
        }

        if (airport.iata && airport.iata.trim() !== '') {
            filters.push({
                field: 'iata',
                operator: 'neq',
                value: airport.iata
            });
        }

        return filters;
    }, []);

    const createCompositeFilter = useCallback((filters: Array<FilterDescriptor | CompositeFilterDescriptor>): CompositeFilterDescriptor => {
        return {
            logic: 'and',
            filters: filters
        };
    }, []);

    const createDataSourceRequestExcludingAirports = useCallback(
        (originAirport?: AirportInfo | null, destinationAirport?: AirportInfo | null, maxResults?: number) => {
            const filterDescriptors = [];

            if (originAirport) {
                const originFilters = createAirportExclusionFilters(originAirport);
                if (originFilters.length !== 0) {
                    filterDescriptors.push(createCompositeFilter(originFilters));
                }
            }

            if (destinationAirport) {
                const destinationFilters = createAirportExclusionFilters(destinationAirport);
                if (destinationFilters.length !== 0) {
                    filterDescriptors.push(createCompositeFilter(destinationFilters));
                }
            }

            const dataSource: State = {
                take: maxResults ?? 1,
                skip: 0
            };

            if (filterDescriptors.length !== 0) {
                dataSource.filter = createCompositeFilter(filterDescriptors);
            }

            return dataSource;
        },
        [createAirportExclusionFilters, createCompositeFilter]
    );

    const getNearestAirports = useCallback(
        async (
            position: Coordinate,
            maxResults?: number,
            excludeAirports?: (AirportInfo | null | undefined)[]
        ): Promise<AirportWithDistance[] | null> => {
            if (!position.isValid) {
                return null;
            }

            const validExcludeAirports = excludeAirports?.filter((a): a is AirportInfo => a != null) ?? [];

            const dataSource = createDataSourceRequestExcludingAirports(validExcludeAirports[0], validExcludeAirports[1], maxResults);

            try {
                const nearestAirportsResult = await worker.actor.sendAsync({
                    type: MessageType.getNearestAirports,
                    data: {
                        lat: position.lat,
                        lng: position.lng,
                        state: dataSource
                    }
                });
                const nearestAirports = nearestAirportsResult.data as AirportWithDistance[];

                if (nearestAirports && nearestAirports.length > 0) {
                    return nearestAirports;
                }
            } catch (err) {
                console.error('Failed to fetch nearest airports:', err);
            }

            return null;
        },
        [createDataSourceRequestExcludingAirports, worker]
    );

    const getNearestAirport = useCallback(
        async (position: Coordinate, excludeAirports?: (AirportInfo | null | undefined)[]): Promise<AirportWithDistance | null> => {
            if (!position.isValid) {
                return null;
            }

            const nearestAirports = await getNearestAirports(position, 1, excludeAirports);
            if (nearestAirports && nearestAirports.length > 0) {
                return nearestAirports[0];
            }

            return null;
        },
        [getNearestAirports]
    );

    const fetchCurrentPosition = useCallback(
        async (acid: string): Promise<FlightCurrentPosition | null> => {
            try {
                const currentPosData = await worker.actor.sendAsync({
                    type: MessageType.getFlightCurrentLocationInfo,
                    data: { acid }
                });

                if (!currentPosData) {
                    return null;
                }

                const result = currentPosData as FlightCurrentPosition;

                result.nearestCity = await getNearestCity(currentPosData.position);
                result.nearestAirport = await getNearestAirport(currentPosData.position);

                result.destinationAirport = {
                    name: currentPosData.destinationAirport?.name,
                    location: new Coordinate(currentPosData.destinationAirport?.lat ?? 200, currentPosData.destinationAirport?.lng ?? 200),
                    icao: currentPosData.destinationAirport?.icao,
                    iata: currentPosData.destinationAirport?.iata,
                    faa: currentPosData.destinationAirport?.faa
                };

                result.originAirport = {
                    name: currentPosData.originAirport?.name,
                    location: new Coordinate(currentPosData.originAirport?.lat ?? 200, currentPosData.originAirport?.lng ?? 200),
                    icao: currentPosData.originAirport?.icao,
                    iata: currentPosData.originAirport?.iata,
                    faa: currentPosData.originAirport?.faa
                };

                result.arrivalTime = currentPosData.arrivalTime ? new Date(currentPosData.arrivalTime) : null;

                return result;
            } catch (err) {
                console.error('Failed to fetch current position:', err);
                return null;
            }
        },
        [getNearestCity, getNearestAirport, worker]
    );

    const fetchLastKnownLocation = useCallback(
        async (acid: string): Promise<LastKnownLocationInfo | null> => {
            try {
                const lklResponse = await LocationApiService.lastKnownLocation(
                    {
                        body: {
                            id: acid,
                            type: GetLastKnowLocationTypeEnum.acid
                        }
                    },
                    { withAuthorization: true }
                );

                if (!lklResponse) {
                    return null;
                }

                const position = new Coordinate(lklResponse.latitude ?? 200, lklResponse.longitude ?? 200);

                const [originAirport, destinationAirport, nearestCity] = await Promise.all([
                    getAirportInfo(lklResponse.origin),
                    getAirportInfo(lklResponse.destination),
                    lklResponse.nearestCity && lklResponse.distanceFromCity != null && lklResponse.bearingFromCity != null
                        ? Promise.resolve({
                              name: lklResponse.nearestCity,
                              distance: lklResponse.distanceFromCity,
                              fromBearing: lklResponse.bearingFromCity
                          })
                        : Promise.resolve(null)
                ]);

                const nearestAirport = await getNearestAirport(position, [originAirport, destinationAirport]);

                const distanceToDestination =
                    position.isValid && destinationAirport ? position.distanceToAirport(destinationAirport) : null;

                return {
                    position,
                    time: lklResponse.dateAtLoc ? new Date(lklResponse.dateAtLoc) : null,
                    origin: lklResponse.origin ?? null,
                    destination: lklResponse.destination ?? null,
                    originAirport: originAirport ?? null,
                    destinationAirport,
                    distanceToDestination,
                    isAtDestination: Boolean(lklResponse.isAtDest),
                    nearestCity: nearestCity ?? null,
                    nearestAirport
                };
            } catch (err) {
                console.error('Failed to fetch last known location:', err);
                return null;
            }
        },
        [getAirportInfo, getNearestAirport]
    );

    const fetchFlightPosition = useCallback(
        async (acid: string) => {
            setIsLoading(true);
            setError(null);

            try {
                const [current, lastKnown] = await Promise.all([fetchCurrentPosition(acid), fetchLastKnownLocation(acid)]);

                setCurrentPosition(current);
                setLastKnownLocation(lastKnown);
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to fetch flight position');
                setError(error);
                console.error('Error fetching flight position:', err);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchCurrentPosition, fetchLastKnownLocation]
    );

    const clear = useCallback(() => {
        setCurrentPosition(null);
        setLastKnownLocation(null);
        setError(null);
    }, []);

    return {
        currentPosition,
        lastKnownLocation,
        isLoading,
        error,
        fetchFlightPosition,
        clear
    };
}
